import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { Grid } from '@mui/material';

import { Box } from 'styles/layout';
import FormWrapper from 'shared/components/forms/FormWrapper/FormWrapper';
import InputField from 'shared/components/forms/InputField/InputField';
import Select from 'shared/components/forms/Select';
import DatePicker from 'shared/components/forms/DatePicker/DatePicker';
import { useFormState } from 'shared/hooks';

import { schema, mapForm2Api, FormFields, genderOptions } from './utils';

const Hl7PatientForm = ({
  onSubmit,
  onCancel,
  initValues = null,
  disabled = false,
  hideButtons = false,
}) => {
  const isEditMode = Boolean(initValues?.id);
  const {
    register,
    control,
    handleSubmit,
    getFieldError,
    setFormValues,
    canSubmitForm,
  } = useFormState(schema, { stateSlice: 'admin', showAsSnackbar: true });

  useEffect(() => {
    if (!initValues) return;
    setFormValues(initValues);
  }, [initValues, setFormValues]);

  const onFormSubmit = React.useCallback(
    (submitData) => {
      onSubmit(mapForm2Api(submitData));
    },
    [onSubmit],
  );

  return (
    <FormWrapper
      formId="hl7PatientForm"
      hideButtons={hideButtons}
      onSubmit={handleSubmit(onFormSubmit)}
      onCancel={() => {
        setFormValues();
        onCancel();
      }}
      formDisabled={disabled}
      isSubmitDisabled={!canSubmitForm}
    >
      <Grid container spacing={3}>
        {/* Row 1 */}
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.siteCode)}
            label="Site Code"
            required={!isEditMode}
            disabled={disabled || isEditMode}
            error={getFieldError(FormFields.siteCode)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.mrn)}
            label="MRN"
            required={!isEditMode}
            disabled={disabled || isEditMode}
            error={getFieldError(FormFields.mrn)}
          />
        </Grid>

        {/* Row 2 */}
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.alternateMrn)}
            label="Alternate MRN"
            disabled={disabled}
            error={getFieldError(FormFields.alternateMrn)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.sendingFacility)}
            label="Sending Facility"
            required
            disabled={disabled}
            error={getFieldError(FormFields.sendingFacility)}
          />
        </Grid>

        {/* Row 3 - Name fields */}
        <Grid item xs={12} md={6}>
          <Grid container spacing={3}>
            <Grid item xs={3}>
              <InputField
                {...register(FormFields.prefix)}
                label="Prefix"
                disabled={disabled}
                error={getFieldError(FormFields.prefix)}
              />
            </Grid>
            <Grid item xs={9}>
              <InputField
                {...register(FormFields.givenName)}
                label="Given Name"
                required
                disabled={disabled}
                error={getFieldError(FormFields.givenName)}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.middleName)}
            label="Middle Name"
            disabled={disabled}
            error={getFieldError(FormFields.middleName)}
          />
        </Grid>

        {/* Row 4 */}
        <Grid item xs={12} md={6}>
          <Grid container spacing={3}>
            <Grid item xs={9}>
              <InputField
                {...register(FormFields.familyName)}
                label="Family Name"
                required
                disabled={disabled}
                error={getFieldError(FormFields.familyName)}
              />
            </Grid>
            <Grid item xs={3}>
              <InputField
                {...register(FormFields.suffix)}
                label="Suffix"
                disabled={disabled}
                error={getFieldError(FormFields.suffix)}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box>
            <DatePicker
              control={control}
              name={FormFields.dob}
              label="Date of Birth"
              required
              disabled={disabled}
              error={getFieldError(FormFields.dob)}
            />
          </Box>
        </Grid>

        {/* Row 5 */}
        <Grid item xs={12} md={6}>
          <Select
            control={control}
            name={FormFields.gender}
            label="Gender"
            disabled={disabled}
            options={genderOptions}
            error={getFieldError(FormFields.gender)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.maritalStatus)}
            label="Marital Status"
            disabled={disabled}
            error={getFieldError(FormFields.maritalStatus)}
          />
        </Grid>

        {/* Row 6 */}
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.religion)}
            label="Religion"
            disabled={disabled}
            error={getFieldError(FormFields.religion)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.phoneNumberHome)}
            label="Home Phone"
            disabled={disabled}
            error={getFieldError(FormFields.phoneNumberHome)}
          />
        </Grid>

        {/* Row 7 */}
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.emailAddressHome)}
            label="Home Email"
            disabled={disabled}
            error={getFieldError(FormFields.emailAddressHome)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.phoneNumberBusiness)}
            label="Business Phone"
            disabled={disabled}
            error={getFieldError(FormFields.phoneNumberBusiness)}
          />
        </Grid>

        {/* Row 8 */}
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.emailAddressBusiness)}
            label="Business Email"
            disabled={disabled}
            error={getFieldError(FormFields.emailAddressBusiness)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.street)}
            label="Street"
            disabled={disabled}
            error={getFieldError(FormFields.street)}
          />
        </Grid>

        {/* Row 9 */}
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.city)}
            label="City"
            disabled={disabled}
            error={getFieldError(FormFields.city)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.province)}
            label="Province/State"
            disabled={disabled}
            error={getFieldError(FormFields.province)}
          />
        </Grid>

        {/* Row 10 */}
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.zip)}
            label="ZIP Code"
            disabled={disabled}
            error={getFieldError(FormFields.zip)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.countryCode)}
            label="Country Code"
            disabled={disabled}
            error={getFieldError(FormFields.countryCode)}
          />
        </Grid>

        {/* Row 11 */}
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.ssn)}
            label="SSN"
            disabled={disabled}
            error={getFieldError(FormFields.ssn)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.dln)}
            label="Driver License Number"
            disabled={disabled}
            error={getFieldError(FormFields.dln)}
          />
        </Grid>

        {/* Row 12 */}
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.raceId)}
            label="Race ID"
            disabled={disabled}
            error={getFieldError(FormFields.raceId)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.raceText)}
            label="Race Text"
            disabled={disabled}
            error={getFieldError(FormFields.raceText)}
          />
        </Grid>

        {/* Row 13 */}
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.ethnicGroupId)}
            label="Ethnic Group ID"
            disabled={disabled}
            error={getFieldError(FormFields.ethnicGroupId)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.ethnicGroupText)}
            label="Ethnic Group Text"
            disabled={disabled}
            error={getFieldError(FormFields.ethnicGroupText)}
          />
        </Grid>

        {/* Row 14 */}
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.primaryLanguageId)}
            label="Primary Language ID"
            disabled={disabled}
            error={getFieldError(FormFields.primaryLanguageId)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.primaryLanguageText)}
            label="Primary Language"
            disabled={disabled}
            error={getFieldError(FormFields.primaryLanguageText)}
          />
        </Grid>

        {/* Row 15 */}
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.publicityIndicator)}
            label="Publicity Indicator"
            disabled={disabled}
            error={getFieldError(FormFields.publicityIndicator)}
          />
        </Grid>
      </Grid>
    </FormWrapper>
  );
};

Hl7PatientForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  initValues: PropTypes.shape({}),
  disabled: PropTypes.bool,
  hideButtons: PropTypes.bool,
};

export default Hl7PatientForm;
